import type { SlaBusinessHours } from '../types/ticket-sla'

// =====================
// Business-hours SLA clock
// =====================
//
// Pure helpers used by the API (computing slaDueAt / slaFirstResponseDueAt at
// ticket creation) and reusable by web SLA chips. The SLA clock only runs
// inside the configured window: `days` (0 Sunday – 6 Saturday), and
// [startHour, endHour) in `timeZone` (endHour may be 24). Anything outside is
// skipped, so "4 business hours" filed Friday 16:00 with a 9–17 window lands
// Monday 12:00.
//
// Implementation notes: we deliberately avoid a timezone library. Wall-clock
// parts for an instant in the target zone come from Intl.DateTimeFormat, and
// time is advanced instant-by-instant (consume the rest of today's window,
// hop hour-by-hour across closed periods). DST transitions that land inside a
// window can skew a boundary by up to an hour — acceptable for SLA targets.

interface ZoneParts {
  weekday: number // 0 (Sunday) – 6 (Saturday)
  hour: number
  minute: number
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function getZoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    })
    formatterCache.set(timeZone, formatter)
  }
  return formatter
}

function getZoneParts(instant: Date, timeZone: string): ZoneParts {
  const parts = getZoneFormatter(timeZone).formatToParts(instant)
  let weekday = 0
  let hour = 0
  let minute = 0
  for (const part of parts) {
    if (part.type === 'weekday') weekday = WEEKDAY_INDEX[part.value] ?? 0
    else if (part.type === 'hour') hour = Number(part.value)
    else if (part.type === 'minute') minute = Number(part.value)
  }
  return { weekday, hour, minute }
}

/** Is the calendar's window well-formed enough to run a clock against? */
export function isUsableBusinessHours(
  businessHours: SlaBusinessHours | null | undefined,
): businessHours is SlaBusinessHours {
  if (!businessHours) return false
  if (!businessHours.timeZone) return false
  if (!businessHours.days?.length) return false
  if (businessHours.endHour <= businessHours.startHour) return false
  try {
    getZoneFormatter(businessHours.timeZone)
  } catch {
    return false
  }
  return true
}

/** Is this instant inside the calendar's working window? */
export function isWithinBusinessHours(
  instant: Date,
  businessHours: SlaBusinessHours,
): boolean {
  const { weekday, hour } = getZoneParts(instant, businessHours.timeZone)
  if (!businessHours.days.includes(weekday)) return false
  return hour >= businessHours.startHour && hour < businessHours.endHour
}

// Hard cap on the walk so a degenerate calendar (or absurd target) can never
// spin: ~2 years of hourly hops.
const MAX_STEPS = 24 * 730

/**
 * Add `minutes` of working time to `start`, where the clock only runs inside
 * the business-hours window. Without a usable calendar this is plain
 * wall-clock addition. The walk consumes what remains of the current open
 * window, then hops hour-by-hour over closed time; if the safety cap is ever
 * hit it falls back to wall-clock so a due date is always produced.
 */
export function addBusinessMinutes(
  start: Date,
  minutes: number,
  businessHours?: SlaBusinessHours | null,
): Date {
  if (!Number.isFinite(minutes) || minutes <= 0) return new Date(start)
  if (!isUsableBusinessHours(businessHours)) {
    return new Date(start.getTime() + minutes * 60_000)
  }

  let remaining = Math.ceil(minutes)
  let cursor = new Date(start)
  for (let step = 0; step < MAX_STEPS; step++) {
    const parts = getZoneParts(cursor, businessHours.timeZone)
    const inWindow =
      businessHours.days.includes(parts.weekday) &&
      parts.hour >= businessHours.startHour &&
      parts.hour < businessHours.endHour
    if (inWindow) {
      // Minutes left in today's window from the cursor position.
      const minuteOfDay = parts.hour * 60 + parts.minute
      const available = businessHours.endHour * 60 - minuteOfDay
      if (remaining <= available) {
        return new Date(cursor.getTime() + remaining * 60_000)
      }
      remaining -= available
      cursor = new Date(cursor.getTime() + available * 60_000)
    } else {
      // Closed: hop to the next hour boundary and re-check.
      const hopMinutes = 60 - parts.minute || 60
      cursor = new Date(cursor.getTime() + hopMinutes * 60_000)
    }
  }
  return new Date(start.getTime() + minutes * 60_000)
}
